/* eslint-disable react/jsx-no-bind */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as FacilityIcon } from '../../../../assets/icons/Device.svg';
import { ReactComponent as EditPencilCard } from '../../../../assets/icons/edit-pencil.svg';
import { ReactComponent as LocationIcon } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../../assets/icons/sidebar/sidebar-account.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/tables/expand-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { PortalType } from '../../../../config/enum';
import { usePortal } from '../../../../contexts/PortalContext/PortalContext';
import {
  AvailableStation,
  RequestStationInterface,
} from '../../../../interface/partner-interfaces/location-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import { setPageTitle } from '../../../../redux/slices/PageTitleSlice';
import {
  fetchListFilters,
  fetchRejectedStations,
  setRejectLocationFilterData,
  setSortData,
  updateLocationFilters,
  refreshRejectedStations,
} from '../../../../redux/slices/Partners/RejectedLocationManagementSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationApi from '../../../../utils/api/partner/station';

import AddLocation from './AddLocation';

interface RejectedListProperties {
  preventNavigation?: boolean;
  fetchByPartner?: string;
  updateCounts?: () => void;
  onSuccess?: () => void;
}

/**
 * Add new station component
 * @param {Readonly<RejectedListProperties>} param0 - The component properties
 * @param {boolean} [param0.preventNavigation] - The preventNavigation prop
 * @param {string} [param0.fetchByPartner] - Account custom ID to filter stations by, when scoped to a single partner
 * @param {() => void} [param0.updateCounts] - The updateCounts callback prop
 * @param {() => void} [param0.onSuccess] - The onSuccess callback prop
 * @returns {any} Rejected list component
 */
function RejectedList({
  preventNavigation = false,
  fetchByPartner,
  updateCounts,
  onSuccess,
}: Readonly<RejectedListProperties>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartnerPortal = localStorage.getItem('activePortal') === 'partner';
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { t, i18n } = useTranslation('location');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const {
    stations,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortBy,
    sortField,
    isFilterUpdated,
  } = useAppSelector((state: RootState) => state.partnersRejectLocation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.partnersFacilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const { portalType } = usePortal();

  // /**
  //  * Navigates to the station dashboard.
  //  * @param {any} stationId The station custom ID
  //  */
  // const handleExpandStation = (stationId: string | any) => {
  //   const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
  //   if (!stationDetails) return;
  //   navigate(
  //     `/ev-admin/location-management/${stationDetails._id}/location-dashboard?searchBy=${stationDetails.name}`,
  //     { state: { source: window.location.pathname + window.location.search } },
  //   );
  // };

  // /**
  //  * Opens the station dashboard in a new tab.
  //  * @param stationId The station custom ID
  //  */
  // const handleOpenStationInNewTab = (stationId: string | any) => {
  //   const stationDetails = stations.find((value: AvailableStation) => value._id === stationId);
  //   if (!stationDetails) return;
  //   window.open(
  //     `/ev-admin/station-expanded/${stationDetails._id}/location-dashboard?searchBy=${stationDetails.name}&source=${window.location.pathname}${window.location.search}`,
  //     '_blank',
  //   );
  // };

  /**
   * Set the target station and open the delete confirmation modal
   */
  const handleDeleteClick = (stationId?: string) => {
    setSelectedStationId(stationId || null);
    setDeleteModalOpen(true);
  };

  /**
   * Function to open account details page
   * @param {any} selectedRow - The selectedRow
   */
  const handleOpenAccountDetails = (selectedRow: AvailableStation) => {
    try {
      if (!selectedRow?.accountCustomId) {
        throw new Error('Account ID not found');
      }

      navigate(
        `/ev-admin/accounts/partners/details/payout-management/${selectedRow.accountCustomId}`,
        {
          state: {
            source: location.pathname + location.search,
          },
        },
      );
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 400,
            message: error?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };
  /**
   * Handle the fetch data for Api call function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestStationInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);
    console.log(selectedFacility?._id, portalType, PortalType);

    if (!loading) {
      dispatch(
        fetchRejectedStations({
          ...data,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      );
    }
  };
  /**
   * Auto refresh the table data
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshRejectedStations({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
      }),
    );
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
    });
    if (updateCounts) {
      updateCounts();
    }
  };

  /**
   * Handle changes to edit mode by updating state and URL parameter
   */
  const handleEditModeChange = (isEdit: boolean) => {
    setIsEditMode(isEdit);
    const newParameters = new URLSearchParams(searchParameters);
    if (isEdit) {
      newParameters.set('action', 'edit');
    } else {
      newParameters.delete('action');
    }
    setSearchParameters(newParameters);
  };

  const column = [
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableStation,
      sortAllow: true,
      /**
       * No of stations
       * @param {any} currentRow - The currentRow
       */
      customData: (currentRow: AvailableStation) => (
        <p className="hyper-link-coloum">{currentRow.name || '-'}</p>
      ),
    },
    ...(!isPartnerPortal
      ? [
          {
            label: t('facilityName'),
            accessorKey: 'accountName' as keyof AvailableStation,
            sortAllow: true,
            /**
             * Facility name view in table
             * @param row
             * @returns
             */
          },
          {
            label: t('facilityAccountId'),
            accessorKey: 'accountCustomId' as keyof AvailableStation,
            sortAllow: true,
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={setIsEditMode}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  setIsEditMode(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
            /**
             * Facility name view in table
             * @param row
             * @returns
             */
          },
        ]
      : []),

    {
      label: t('Address'),
      accessorKey: 'address' as keyof AvailableStation,
      sortAllow: false,
      columnWidth: 250,

      /**
       * Location view in table
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
    {
      label: t('rejectReason'),
      accessorKey: 'reason' as keyof AvailableStation,
      /**
       * Location view in table
       * @param {any} station - The station
       */
      customData: (station: AvailableStation) =>
        station.submission ? (
          <div className="location-in-station-table">
            {/* <span>
              <CloseCircle />
            </span> */}
            <p className="table-row-each-data">{station.submission.reason}</p>
          </div>
        ) : null,
    },
    ...(portalType === PortalType.PARTNER
      ? [
          {
            label: t('actions'),
            accessorKey: '_id' as keyof AvailableStation,
            columnWidth: 180,
            sortAllow: false,
            hideWhenSidePanelOpen: true,
            sticky: 'right' as const,
            /**
             * Row level actions - expand, open in new tab, edit and delete
             * @param {AvailableStation} station - The station data - The station row to render actions for
             */
            customData: (station: AvailableStation): React.JSX.Element => {
              return (
                <div className="action-buttons-wrap">
                  {station?._id && (
                    <>
                      {/* <div
                  role="presentation"
                  className="action-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleExpandStation(station._id);
                  }}>
                  <ExpandIcon className="icon expand" />
                </div> */}
                      {/* <div
                  role="presentation"
                  className="action-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenStationInNewTab(station._id);
                  }}>
                  <NewTabIcon className="icon new-tab" />
                </div> */}
                      <div
                        role="presentation"
                        className={`action-button ${
                          !permissionCheck('partnerChargingStationUpdate') &&
                          !permissionCheck('cpoChargingStationUpdate')
                            ? 'disable-action'
                            : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();

                          if (
                            !permissionCheck('partnerChargingStationUpdate') &&
                            !permissionCheck('cpoChargingStationUpdate')
                          ) {
                            return;
                          }
                          handleEditModeChange(true);
                          const parameters = new URLSearchParams(searchParameters);
                          parameters.delete('accountCustomId');
                          parameters.set('stationId', station._id as string);
                          setSearchParameters(parameters);
                          setSidePanelOpen(false);
                        }}>
                        <EditPencilCard className="icon edit" />
                      </div>
                      <div
                        role="presentation"
                        className={`action-button ${
                          !permissionCheck('partnerChargingStationDelete') &&
                          !permissionCheck('cpoChargingStationDelete')
                            ? 'disable-action'
                            : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (
                            !permissionCheck('partnerChargingStationDelete') &&
                            !permissionCheck('cpoChargingStationDelete')
                          ) {
                            return;
                          }
                          handleDeleteClick(station._id);
                        }}>
                        <DeleteIcon className="icon delete" />
                      </div>
                    </>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  /**
   * Function to handle page change
   * @param {any} sortData - The sortData
   */
  // const handlePageChange = (pageNumber: number) => {
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   fetchData({
  //     ...queryParameters,
  //     sortField,
  //     sortBy,
  //     page: pageNumber,
  //     limit,
  //   });
  // };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setRejectLocationFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   * @param {any} value - The value
   */
  const handleLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: 1,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'name' },
    {
      label: t('facilityName'),
      accessorKey: 'accountName',
    },
    {
      label: t('facilityAccountId'),
      accessorKey: 'accountCustomId',
    },
    { label: t('address'), accessorKey: 'address' },
    { label: t('rejectReason'), accessorKey: 'reason' },
    { label: t('created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    stationApi
      .fetchStationsExport(
        removeEmptyKeys({
          ...queryParameters,
          submissionStatus: 'rejected',
          sortBy,
          sortField,
          ...(fetchByPartner ? { accountCustomIds: [fetchByPartner] } : {}),
        }),
      )
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AvailableStation, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'opensAt':
                return `${FormatUtcString(row.opensAt)} - ${FormatUtcString(row.closesAt)}`;
              case 'reason':
                return row.submission?.reason || 'N/A';
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt);
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt);
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'rejected-station-list.csv');
          } else {
            exportPDF(headers, rows, 'rejected-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the open side panel on row click function
   * @param {any} _ - The facility tag data
   * @param {any} row - The row
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: AvailableStation) => {
    if (!row?._id) return;

    searchParameters.delete('accountCustomId');
    // Set or update the Id param
    searchParameters.set('stationId', row?._id);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
    if (!preventNavigation) {
      dispatch(setPageTitle({ title: t('facility'), icon: <FacilityIcon />, count: undefined }));
    }
  };

  /**
   * Returns side panel header content: title and expand actions.
   * @returns {any} Side panel top left content
   */
  const renderSidePanelTopLeftContent = (): React.JSX.Element | null => {
    if (sidePanelOpen) {
      return <p className="side-panel-header-title">{t('addStation')}</p>;
    }

    const accountCustomId = searchParameters.get('accountCustomId');
    if (accountCustomId) {
      return (
        <div className="side-panel-header-left-title-actions">
          <p className="side-panel-header-title">
            {isEditMode ? t('account:editFacility') : t('account:facilityDetails')}
          </p>
          <div className="side-panel-header-left-icons">
            {!isEditMode && (
              <>
                {/* Expand Pages */}
                <div className="button-wrap">
                  <span
                    role="presentation"
                    className="access-facility"
                    onClick={() =>
                      handleOpenAccountDetails({ accountCustomId } as AvailableStation)
                    }>
                    <ExpandIcon className="icon" />
                  </span>
                  <div className="hover-label">{t('sidePanel:expandPages')}</div>
                </div>
                {permissionCheck('cpoPartnerUpdate') && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleEditModeChange(true)}>
                      <EditPencilCard className="icon" />
                    </span>
                    <div className="hover-label">{t('edit', 'Edit')}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    const stationId = searchParameters.get('stationId');
    if (!stationId) return null;

    const showEditDelete = portalType === PortalType.PARTNER;

    return (
      <div className="side-panel-header-left-title-actions">
        <p className="side-panel-header-title">
          {isEditMode ? t('editLocationDetails') : t('stationDetails')}
        </p>
        <div className="side-panel-header-left-icons">
          {!isEditMode && (
            <>
              {/* Edit Icon */}
              {showEditDelete &&
                (permissionCheck('partnerChargingStationUpdate') ||
                  permissionCheck('cpoChargingStationUpdate')) && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleEditModeChange(true)}>
                      <EditPencilCard className="icon" />
                    </span>
                    <div className="hover-label">{t('edit', 'Edit')}</div>
                  </div>
                )}

              {/* Delete Icon */}
              {showEditDelete &&
                (permissionCheck('partnerChargingStationDelete') ||
                  permissionCheck('cpoChargingStationDelete')) && (
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => handleDeleteClick(stationId)}>
                      <DeleteIcon className="icon delete" />
                    </span>
                    <div className="hover-label">{t('delete')}</div>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    );
  };

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = async () => {
    if (selectedStationId) {
      try {
        await stationApi.deleteStation(selectedStationId);
        dispatch(
          setNotificationDetails({
            type: 'success',
            notificationDetails: {
              statusCode: 200,
              message: t('stationDeletedSuccessfully'),
            },
          }),
        );
        handleRefresh();
        setDeleteModalOpen(false);
        // Close side panel
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('stationId');
        setSearchParameters(parameters);
      } catch (error: any) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      }
    }
  };

  /**
   * Add account function
   */
  const addStation = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('stationId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  const previousFetchByPartner = useRef<string | undefined>();

  const isPartnerDetailsPage = document.location.pathname.includes('accounts/partners/details');

  /**
   * Handling dynamic table heights
   * @returns {string} returns dynamic table height
   */
  const handleHeight = (): number => {
    if (isPartnerDetailsPage) {
      return 500;
    }

    return 330;
  };

  useEffect(() => {
    const hasPartnerChanged = previousFetchByPartner.current !== fetchByPartner;

    if (isFilterUpdated || stations.length === 0 || hasPartnerChanged) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });
      previousFetchByPartner.current = fetchByPartner;
    }
  }, [currentFacilityTagsList, filters, fetchByPartner]);

  useEffect(() => {
    if (preventNavigation) {
      return;
    }
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('chargingStations'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t, preventNavigation]);
  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      (!window.location.pathname.includes('payout-management') &&
        searchParameters.get('accountCustomId'))
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      if (!window.location.pathname.includes('payout-management')) {
        parameters.delete('accountCustomId');
      }
      setSearchParameters(parameters, { replace: true });
    }
  }, []);
  return (
    <>
      <ModalBox
        title={t('deleteStation')}
        header={false}
        open={deleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
        content={
          <DeleteWarning
            title={t('deleteStation')}
            description={t('deleteStationWarning')}
            cancelButtonOnClick={() => setDeleteModalOpen(false)}
            submitButtonOnClick={handleConfirmDelete}
            selectedRowId={selectedStationId}
          />
        }
      />

      <div className="rejected-list-table-container" dir={i18n.dir()}>
        <CustomTable
          dataKey="StationRejected"
          loading={loading}
          limit={limit}
          header={column}
          data={stations}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          page={page}
          height={handleHeight()}
          handleSortChange={handleSortFunction}
          // handlePageChange={handlePageChange}
          pageLimitChange={handleLimitChange}
          exportDataPdfOrCSV={
            permissionCheck('partnerChargingStationExport') ||
            permissionCheck('cpoChargingStationExport')
              ? exportDataPdfOrCSV
              : undefined
          }
          sizeChangeButtonRequired
          exportLoader={exportLoader}
          filters={filters}
          filtersToDispatch={updateLocationFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey={searchParameters.get('deviceId') ? 'deviceId' : 'stationId'}
          rowClickSidePanel={
            searchParameters.get('deviceId') ? (
              <DeviceSidePanel
                fetchIdFrom="queryParams"
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                  onSuccess?.();
                }}
              />
            ) : (
              <LocationSidePanel
                fetchIdFrom="queryParams"
                allowList={false}
                initialIsEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                onDeleteStation={handleDeleteClick}
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                  onSuccess?.();
                }}
              />
            )
          }
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={
            <AddLocation
              onClose={() => setSidePanelOpen(false)}
              onSuccess={() => {
                handleRefresh();
                onSuccess?.();
              }}
              fetchByPartner={fetchByPartner}
            />
          }
          onSidePanelClose={() => {
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('stationId');
            parameters.delete('deviceId');
            parameters.delete('accountCustomId'); // close row panel if open
            parameters.delete('action');
            setSearchParameters(parameters);

            setSidePanelOpen(false);
            setIsEditMode(false);
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          rightTopContent={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <MyButton
                  label={t('addStation')}
                  buttonType="submit"
                  onClickFunc={addStation}
                  svgIcon={<PlusIcon />}
                  disableButton={
                    !permissionCheck('partnerChargingStationAdd') &&
                    !permissionCheck('cpoChargingStationAdd')
                  }
                />
              </div>
            </div>
          }
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          created
          lastUpdated
        />
      </div>
    </>
  );
}

RejectedList.defaultProps = {
  preventNavigation: false,
  fetchByPartner: undefined,
  updateCounts: undefined,
  onSuccess: undefined,
};

export default RejectedList;
